import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Lock } from 'lucide-react';
import { signIn, hasValidSession, installAdminAuth } from '@/lib/adminAuth';

installAdminAuth();

export const ProtectedAdminRoute = ({ children }) => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // A token from earlier in this browser session is only good if the server
    // still accepts it, so ask rather than assume.
    let live = true;
    hasValidSession().then((ok) => {
      if (!live) return;
      setIsAuthenticated(ok);
      setIsLoading(false);
    });
    return () => { live = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await signIn(password);
      setIsAuthenticated(true);
      setError('');
    } catch (err) {
      const status = err.response?.status;
      setError(
        status === 503
          ? 'Admin access is not set up on the server yet. Please contact the site owner.'
          : 'Incorrect password. Please try again.'
      );
      setPassword('');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#D4AF37]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="max-w-md w-full glass-card rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="text-[#D4AF37]" size={32} />
            </div>
            <h1 className="font-heading text-3xl text-[#F5F5DC] mb-2">Admin Access</h1>
            <p className="text-[#A1A1AA] text-sm">
              Please enter the password to access the admin portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#F5F5DC] mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2A2A2A] rounded-lg text-[#EDEDED] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="Enter admin password"
                autoFocus
              />
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center bg-red-400/10 border border-red-400/20 rounded-lg py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="btn-gold rounded-full w-full py-3 font-semibold disabled:opacity-60"
            >
              {isSubmitting ? 'Checking…' : 'Access Admin Portal'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-[#A1A1AA] hover:text-[#D4AF37] text-sm transition-colors"
            >
              ← Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedAdminRoute;
